import { useState, useEffect, useMemo, useCallback } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Bell, Lock, Trash2, Info, Clock, CalendarOff, Plus, X } from "lucide-react";
import { AGENDA_DAYS, DEFAULT_OPENING_HOURS, normalizeOpeningHours } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    notificationEmail: "docteur@exemple.fr",
    retentionDays: 90
  });

  // --- Horaires d'ouverture & absences (persistés côté serveur) ---
  const userEmail = useMemo(
    () => (JSON.parse(localStorage.getItem('fruitycall_user') || '{}').email || ""),
    []
  );
  const [openingHours, setOpeningHours] = useState(DEFAULT_OPENING_HOURS);
  const [absences, setAbsences] = useState([]);
  const [absenceForm, setAbsenceForm] = useState({ start_date: "", end_date: "", reason: "" });

  const loadAgendaConfig = useCallback(async () => {
    if (!userEmail) return;
    try {
      const response = await fetch(`${API_URL}/api/voice/settings?user_email=${encodeURIComponent(userEmail)}`);
      const data = await response.json();
      setOpeningHours(normalizeOpeningHours(data.opening_hours));
      setAbsences(data.absences || []);
    } catch (error) {
      console.error("Erreur chargement paramètres agenda:", error);
    }
  }, [userEmail]);

  useEffect(() => {
    loadAgendaConfig();
  }, [loadAgendaConfig]);

  const persistAgendaConfig = async (payload, successMessage) => {
    try {
      const response = await fetch(
        `${API_URL}/api/voice/settings?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error();
      toast.success(successMessage);
      return true;
    } catch {
      toast.error("Erreur lors de l'enregistrement");
      return false;
    }
  };

  const updateDay = (dayKey, patch) => {
    setOpeningHours((prev) => ({ ...prev, [dayKey]: { ...prev[dayKey], ...patch } }));
  };

  const updateRange = (dayKey, index, patch) => {
    setOpeningHours((prev) => {
      const ranges = prev[dayKey].ranges.map((r, i) => (i === index ? { ...r, ...patch } : r));
      return { ...prev, [dayKey]: { ...prev[dayKey], ranges } };
    });
  };

  const addRange = (dayKey) => {
    setOpeningHours((prev) => {
      const ranges = [...prev[dayKey].ranges, { start: "14:00", end: "17:00" }];
      return { ...prev, [dayKey]: { ...prev[dayKey], ranges } };
    });
  };

  const removeRange = (dayKey, index) => {
    setOpeningHours((prev) => {
      const ranges = prev[dayKey].ranges.filter((_, i) => i !== index);
      return {
        ...prev,
        [dayKey]: { ...prev[dayKey], ranges: ranges.length ? ranges : [{ start: "09:00", end: "17:00" }] },
      };
    });
  };

  const saveOpeningHours = () =>
    persistAgendaConfig({ opening_hours: openingHours }, "Horaires d'ouverture enregistrés");

  const addAbsence = async () => {
    if (!absenceForm.start_date || !absenceForm.end_date) {
      toast.error("Indiquez les dates de début et de fin");
      return;
    }
    if (absenceForm.end_date < absenceForm.start_date) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    const next = [
      ...absences,
      { id: Date.now().toString(), ...absenceForm },
    ].sort((a, b) => a.start_date.localeCompare(b.start_date));
    if (await persistAgendaConfig({ absences: next }, "Absence enregistrée")) {
      setAbsences(next);
      setAbsenceForm({ start_date: "", end_date: "", reason: "" });
    }
  };

  const removeAbsence = async (id) => {
    const next = absences.filter((a) => a.id !== id);
    if (await persistAgendaConfig({ absences: next }, "Absence supprimée")) {
      setAbsences(next);
    }
  };

  const formatAbsenceDate = (iso) => {
    const [y, m, d] = (iso || "").split("-");
    return y ? `${d}/${m}/${y}` : iso;
  };
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };
  
  const handleSaveNotifications = () => {
    toast.success("Paramètres de notification mis à jour");
  };
  
  const handleChangePassword = (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    toast.success("Mot de passe modifié avec succès");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };
  
  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <AppHeader />
      
      <main className="flex-1">
        <div className="container-custom py-8">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">Paramètres</h1>
            <p className="text-muted-foreground">
              Gérez les paramètres de votre compte et vos préférences
            </p>
          </div>
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Horaires d'ouverture */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <CardTitle>Horaires d'ouverture</CardTitle>
                </div>
                <CardDescription>
                  Plages d'ouverture du cabinet, affichées sur l'agenda. Ajoutez plusieurs plages
                  par jour pour une pause déjeuner (ex. 9h-12h et 14h-17h).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {AGENDA_DAYS.map(({ key, label }) => {
                  const day = openingHours[key];
                  return (
                    <div key={key} className="flex items-start gap-3">
                      <label className="flex items-center gap-2 w-24 shrink-0 text-sm pt-2">
                        <Checkbox
                          checked={day.open}
                          onCheckedChange={(checked) => updateDay(key, { open: !!checked })}
                        />
                        {label}
                      </label>
                      <div className="flex-1 space-y-1.5">
                        {day.open ? (
                          day.ranges.map((range, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Input
                                type="time"
                                className="w-28"
                                value={range.start}
                                onChange={(e) => updateRange(key, i, { start: e.target.value })}
                              />
                              <span className="text-muted-foreground text-sm">à</span>
                              <Input
                                type="time"
                                className="w-28"
                                value={range.end}
                                onChange={(e) => updateRange(key, i, { end: e.target.value })}
                              />
                              {day.ranges.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-destructive"
                                  onClick={() => removeRange(key, i)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              {i === day.ranges.length - 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 gap-1 px-2 text-muted-foreground"
                                  onClick={() => addRange(key)}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                  plage
                                </Button>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground pt-2">Fermé</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <Button onClick={saveOpeningHours} className="w-full">
                  Enregistrer les horaires
                </Button>
              </CardContent>
            </Card>

            {/* Absences */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarOff className="h-5 w-5 text-primary" />
                  <CardTitle>Absences</CardTitle>
                </div>
                <CardDescription>
                  Congés, formations, fermetures exceptionnelles : ces périodes apparaissent
                  bloquées sur l'agenda.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {absences.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune absence programmée.</p>
                ) : (
                  <div className="space-y-2">
                    {absences.map((absence) => (
                      <div
                        key={absence.id}
                        className="flex items-center justify-between rounded-lg border p-2.5 text-sm"
                      >
                        <div>
                          <span className="font-medium">
                            Du {formatAbsenceDate(absence.start_date)} au{" "}
                            {formatAbsenceDate(absence.end_date)}
                          </span>
                          {absence.reason && (
                            <span className="text-muted-foreground"> — {absence.reason}</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive"
                          onClick={() => removeAbsence(absence.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="rounded-lg border p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="absence-start">Du</Label>
                      <Input
                        id="absence-start"
                        type="date"
                        value={absenceForm.start_date}
                        onChange={(e) =>
                          setAbsenceForm({ ...absenceForm, start_date: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="absence-end">Au (inclus)</Label>
                      <Input
                        id="absence-end"
                        type="date"
                        value={absenceForm.end_date}
                        onChange={(e) =>
                          setAbsenceForm({ ...absenceForm, end_date: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="absence-reason">Motif (optionnel)</Label>
                    <Input
                      id="absence-reason"
                      value={absenceForm.reason}
                      onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })}
                      placeholder="Congés, formation…"
                    />
                  </div>
                  <Button onClick={addAbsence} className="w-full gap-1">
                    <Plus className="h-4 w-4" />
                    Ajouter l'absence
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <CardTitle>Notifications</CardTitle>
                </div>
                <CardDescription>
                  Configurez vos préférences de notification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="email-notifications">Notifications par email</Label>
                    <p className="text-sm text-muted-foreground">
                      Recevez un email pour chaque nouveau message
                    </p>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={settings.emailNotifications}
                    onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                  />
                </div>
                
                {settings.emailNotifications && (
                  <div className="space-y-2">
                    <Label htmlFor="notification-email">Email de notification</Label>
                    <div className="flex gap-2">
                      <Input
                        id="notification-email"
                        type="email"
                        value={settings.notificationEmail}
                        onChange={(e) => handleSettingChange('notificationEmail', e.target.value)}
                        placeholder="votre@email.fr"
                      />
                    </div>
                  </div>
                )}
                
                <Button onClick={handleSaveNotifications} className="w-full">
                  Enregistrer les préférences
                </Button>
              </CardContent>
            </Card>
            
            {/* Password */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  <CardTitle>Mot de passe</CardTitle>
                </div>
                <CardDescription>
                  Modifiez votre mot de passe de connexion
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Mot de passe actuel</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full">
                    Changer le mot de passe
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Data Retention */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  <CardTitle>Conservation des messages</CardTitle>
                </div>
                <CardDescription>
                  Informations sur la conservation de vos données
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-2xl font-bold text-primary">{settings.retentionDays}</span>
                    <span className="text-sm text-muted-foreground">jours</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Les messages vocaux et leurs transcriptions sont conservés pendant {settings.retentionDays} jours par défaut.
                  </p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Vous pouvez archiver ou supprimer manuellement vos messages à tout moment depuis votre boîte de réception.
                </p>
                
                <div className="rounded-lg border border-warning/20 bg-warning/5 p-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Note :</span> La durée de conservation est définie pour respecter les recommandations légales en matière de données de santé.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Danger Zone */}
            <Card className="border-2 border-destructive/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <CardTitle className="text-destructive">Zone dangereuse</CardTitle>
                </div>
                <CardDescription>
                  Actions irréversibles sur votre compte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Supprimer tous les messages</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Supprime définitivement tous vos messages et transcriptions. Cette action est irréversible.
                  </p>
                  <Button 
                    variant="destructive" 
                    onClick={() => toast.error("Fonctionnalité désactivée en mode démo")}
                  >
                    Supprimer tous les messages
                  </Button>
                </div>
                
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Supprimer mon compte</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Supprime définitivement votre compte et toutes vos données. Cette action est irréversible.
                  </p>
                  <Button 
                    variant="destructive"
                    onClick={() => toast.error("Fonctionnalité désactivée en mode démo")}
                  >
                    Supprimer mon compte
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;